import { useState } from 'react';
import styles from './Reports.module.css';

type ReportType = 'revenue' | 'players' | 'games' | 'transactions';

interface Report {
	id: string;
	name: string;
	type: ReportType;
	dateRange: string;
	createdAt: number;
	status: 'ready' | 'processing';
	downloadUrl?: string;
}

const mockReports: Report[] = [
	{ id: '1', name: 'Monthly Revenue - December 2025', type: 'revenue', dateRange: 'Dec 1 - Dec 31, 2025', createdAt: Date.now() - 86400000, status: 'ready', downloadUrl: '#' },
	{ id: '2', name: 'Player Activity - Week 52', type: 'players', dateRange: 'Dec 23 - Dec 29, 2025', createdAt: Date.now() - 172800000, status: 'ready', downloadUrl: '#' },
	{ id: '3', name: 'Game Performance - Q4 2025', type: 'games', dateRange: 'Oct 1 - Dec 31, 2025', createdAt: Date.now() - 3600000, status: 'processing' },
];

export default function Reports() {
	const [reports] = useState<Report[]>(mockReports);
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [newReport, setNewReport] = useState({ type: 'revenue' as ReportType, startDate: '', endDate: '' });

	const handleCreateReport = (e: React.FormEvent) => {
		e.preventDefault();
		// In real app, would call API
		setShowCreateModal(false);
		setNewReport({ type: 'revenue', startDate: '', endDate: '' });
	};

	return (
		<div className={styles.reports}>
			<header className={styles.header}>
				<div>
					<h1 className={styles.title}>Reports</h1>
					<p className={styles.subtitle}>Generate and download performance reports</p>
				</div>
				<button onClick={() => setShowCreateModal(true)} className={styles.createBtn}>
					<PlusIcon />
					Generate Report
				</button>
			</header>

			{/* Quick Stats */}
			<div className={styles.quickStats}>
				<div className={styles.quickStat}>
					<span className={styles.quickStatLabel}>This Month's GGR</span>
					<span className={styles.quickStatValue}>$45,230</span>
				</div>
				<div className={styles.quickStat}>
					<span className={styles.quickStatLabel}>This Month's Bets</span>
					<span className={styles.quickStatValue}>18,450</span>
				</div>
				<div className={styles.quickStat}>
					<span className={styles.quickStatLabel}>Active Players</span>
					<span className={styles.quickStatValue}>2,340</span>
				</div>
			</div>

			{/* Reports List */}
			<div className={styles.section}>
				<h2 className={styles.sectionTitle}>Generated Reports</h2>
				<div className={styles.reportsList}>
					{reports.map((report) => (
						<div key={report.id} className={styles.reportCard}>
							<div className={styles.reportIcon}>
								<ReportIcon type={report.type} />
							</div>
							<div className={styles.reportInfo}>
								<h3 className={styles.reportName}>{report.name}</h3>
								<div className={styles.reportMeta}>
									<span>{report.dateRange}</span>
									<span>•</span>
									<span>Created {formatTime(report.createdAt)}</span>
								</div>
							</div>
							{report.status === 'ready' ? (
								<a href={report.downloadUrl} className={styles.downloadBtn}>
									<DownloadIcon />
									Download
								</a>
							) : (
								<span className={styles.processingBadge}>
									<SpinnerIcon />
									Processing
								</span>
							)}
						</div>
					))}
				</div>
			</div>

			{/* Create Modal */}
			{showCreateModal && (
				<div className={styles.modalOverlay} onClick={() => setShowCreateModal(false)}>
					<div className={styles.modal} onClick={(e) => e.stopPropagation()}>
						<div className={styles.modalHeader}>
							<h2>Generate Report</h2>
							<button onClick={() => setShowCreateModal(false)} className={styles.closeBtn}>
								<CloseIcon />
							</button>
						</div>
						<form onSubmit={handleCreateReport} className={styles.modalForm}>
							<div className={styles.field}>
								<label className={styles.label}>Report Type</label>
								<select value={newReport.type} onChange={(e) => setNewReport({ ...newReport, type: e.target.value as ReportType })} className={styles.select}>
									<option value="revenue">Revenue Report</option>
									<option value="players">Player Activity</option>
									<option value="games">Game Performance</option>
									<option value="transactions">Transactions</option>
								</select>
							</div>
							<div className={styles.dateFields}>
								<div className={styles.field}>
									<label className={styles.label}>Start Date</label>
									<input type="date" value={newReport.startDate} onChange={(e) => setNewReport({ ...newReport, startDate: e.target.value })} required className={styles.input} />
								</div>
								<div className={styles.field}>
									<label className={styles.label}>End Date</label>
									<input type="date" value={newReport.endDate} onChange={(e) => setNewReport({ ...newReport, endDate: e.target.value })} required className={styles.input} />
								</div>
							</div>
							<div className={styles.modalActions}>
								<button type="button" onClick={() => setShowCreateModal(false)} className={styles.cancelBtn}>
									Cancel
								</button>
								<button type="submit" className={styles.submitBtn}>
									Generate
								</button>
							</div>
						</form>
					</div>
				</div>
			)}
		</div>
	);
}

function formatTime(timestamp: number): string {
	const diffMs = Date.now() - timestamp;
	const diffHours = Math.floor(diffMs / 3600000);
	if (diffHours < 24) return `${diffHours}h ago`;
	const diffDays = Math.floor(diffHours / 24);
	return `${diffDays}d ago`;
}

function ReportIcon(_props: { type: ReportType }) {
	return (
		<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
			<polyline points="14 2 14 8 20 8" />
		</svg>
	);
}

function PlusIcon() {
	return (
		<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<line x1="12" y1="5" x2="12" y2="19" />
			<line x1="5" y1="12" x2="19" y2="12" />
		</svg>
	);
}

function DownloadIcon() {
	return (
		<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
			<polyline points="7 10 12 15 17 10" />
			<line x1="12" y1="15" x2="12" y2="3" />
		</svg>
	);
}

function SpinnerIcon() {
	return (
		<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={styles.spinnerIcon}>
			<circle cx="12" cy="12" r="10" opacity="0.25" />
			<path d="M12 2a10 10 0 0 1 10 10" />
		</svg>
	);
}

function CloseIcon() {
	return (
		<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<line x1="18" y1="6" x2="6" y2="18" />
			<line x1="6" y1="6" x2="18" y2="18" />
		</svg>
	);
}
